import RedemptionCodeDisplay from '../RedemptionCodeDisplay';
import React, {useEffect, useState} from 'react'
import {Controller, useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  TextField,
  Typography,
} from '@mui/material'
import OfferSaveActions from '../OfferSaveActions'
import WizardBackButton from '../WizardBackButton'

const codeType = {
  promoCode: 0,
  barcode: 1,
  qrCode: 2,
}

const selectionErrorMessage = 'Select at least one redemption option and enter its value.'

const schema = yup.object().shape({
  promoCodeDefined: yup.boolean().default(false),
  barcodeDefined: yup.boolean().default(false),
  qrCodeDefined: yup.boolean().default(false),

  promoCode: yup.string().when("promoCodeDefined", {
    is: true,
    then: (schema) => schema
      .max(25, "Promo code must be 25 characters or fewer.")
      .required("Enter a promo code."),
    otherwise: (schema) => schema.notRequired(),
  }),

  barcode: yup.string().when("barcodeDefined", {
    is: true,
    then: (schema) => schema.required("Enter a barcode value."),
    otherwise: (schema) => schema.notRequired(),
  }),

  qrCode: yup.string().when("qrCodeDefined", {
    is: true,
    then: (schema) => schema.required("Enter a QR code value."),
    otherwise: (schema) => schema.notRequired(),
  }),
});


export { codeType, schema };


const mapValues = (values) => {
  return {
    promoCode: values.promoCode,
    promoCodeDefined: !!values.promoCode,
    barcode: values.barcode,
    barcodeDefined: !!values.barcode,
    qrCode: values.qrCode,
    qrCodeDefined: !!values.qrCode,
  }
}

const OfferCode = ({
  back,
  onComplete,
  values,
  onSave,
  onUpdate,
  onValuesChange,
  saveButtonLabel,
}) => {
  const [selectionError, setSelectionError] = useState('')
  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isDirty, touchedFields },
    setValue,
    watch,
    getValues,
    trigger,
  } = useForm({
    defaultValues: values.code ? mapValues(values.code) : {},
    resolver: yupResolver(schema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
  })
  const currentValues = watch()

  useEffect(() => {
    const touchedCodes = ['promoCode', 'barcode', 'qrCode']
      .filter((fieldName) => touchedFields[fieldName])
    if (touchedCodes.length > 0) {
      void trigger(touchedCodes)
    }
  }, [
    currentValues.promoCode,
    currentValues.barcode,
    currentValues.qrCode,
    touchedFields,
    trigger,
  ])

  const getDefinedType = (deselected) => {
    if (currentValues.barcodeDefined && codeType.barcode !== deselected) {
      return codeType.barcode
    } else if (currentValues.qrCodeDefined && codeType.qrCode !== deselected) {
      return codeType.qrCode
    } else if (currentValues.promoCodeDefined && codeType.promoCode !== deselected) {
      return codeType.promoCode
    }

    return null;
  }

  const hasDefinedCode = (typeToCheck) => {
    let result = false

    if (codeType.barcode === typeToCheck) {
      result = (currentValues && !!currentValues.barcode) || (values && values.code && !!values.code.barcode)
    } else if (codeType.qrCode === typeToCheck) {
      result = (currentValues && !!currentValues.qrCode) || (values && values.code && !!values.code.qrCode)
    } else if (codeType.promoCode === typeToCheck) {
      result = (currentValues && !!currentValues.promoCode) || (values && values.code && !!values.code.promoCode)
    }

    return result
  }

  const noneSelected = () => {
    return !currentValues.qrCodeDefined && !currentValues.promoCodeDefined && !currentValues.barcodeDefined;
  }

  const updateSelectionError = (changedType, isDefined) => {
    const selections = [
      currentValues.promoCodeDefined,
      currentValues.barcodeDefined,
      currentValues.qrCodeDefined,
    ]
    selections[changedType] = isDefined
    setSelectionError(selections.some(Boolean) ? '' : selectionErrorMessage)
  }

  const [currentType, setCurrentType] = useState(() => {
    return getDefinedType();
  })

  const handleFormSubmit = (data) => {
    const codes = {
      promoCode: data.promoCode,
      barcode: data.barcode,
      qrCode: data.qrCode,
    }
    if (!codes.promoCode && !codes.barcode && !codes.qrCode) {
      setSelectionError(selectionErrorMessage)
      return
    }
    setSelectionError('')
    onComplete(codes)
  }

  const getCodeValue = (data) => ({
    promoCode: data.promoCode,
    barcode: data.barcode,
    qrCode: data.qrCode,
  });

  useEffect(() => {
    onValuesChange?.('code', getCodeValue(currentValues), isDirty);
  }, [currentValues, isDirty, onValuesChange]);

  return (
    <>
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Box mb={4}>
        <Typography variant="h6" component="h2">
          Set your coupon code
        </Typography>
      </Box>
      <Grid container spacing={3}>

        <Grid item xs={12} md={6}>
          <Box>
          <Controller
  name="promoCodeDefined"
  control={control}
  render={({ field }) => (
    <FormControlLabel
      control={
        <Checkbox
          {...field}
          checked={!!field.value} 
          onChange={(e) => {
            const currentValue = e.target.checked;
            field.onChange(currentValue);
            updateSelectionError(codeType.promoCode, currentValue);
            if (!currentValue) {
              setValue("promoCode", "");
              setCurrentType(getDefinedType(codeType.promoCode));
            } else {
              if (noneSelected()) {
                setCurrentType(codeType.promoCode);
              }
            }
          }}
        />
      }
      label="I have a promo code"
    />
  )}
/>


            {
              currentValues.promoCodeDefined &&
              <FormControl fullWidth variant="outlined" error={!!errors.promoCode}>
                <TextField
                  fullWidth
                  label="Promo code"
                  defaultValue=""
                  name="promoCode"
                  {...register("promoCode")}
                  variant="outlined"
                  margin="dense"
                  multiline
                  error={!!errors.promoCode}
                  onInput={
                    (input) => {
                      if (input.target.value) {
                        setCurrentType(codeType.promoCode)
                      }
                    }
                  }
                />
                <FormHelperText error>
                  <Typography component="span" display="flex" justifyContent="space-between">
                    <span>
                      {errors.promoCode ? errors.promoCode.message : ''}
                    </span>
                    <Typography color="textSecondary" variant="caption">
                      {currentValues.promoCode ? currentValues.promoCode.length : 0} / 25
                    </Typography>
                  </Typography>
                </FormHelperText>
              </FormControl>
            }
          </Box>
          <Box>
          <Controller
              name="barcodeDefined"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}  
                      checked={field.value}
                      disabled={currentValues.qrCodeDefined}
                      onChange={(e) => {
                        const currentValue = e.target.checked;
                        field.onChange(currentValue); 
                        updateSelectionError(codeType.barcode, currentValue);
                        if (!currentValue) {
                          setValue("barcode", "");
                          setCurrentType(getDefinedType(codeType.barcode));
                        } else {
                          if (noneSelected()) {
                            setCurrentType(codeType.barcode);
                          }
                        }
                      }}
                    />
                  }
                  label="I have a barcode"
                />
              )}
            />

            {
              currentValues.barcodeDefined &&
              <FormControl fullWidth variant="outlined" error={!!errors.barcode}>
                <TextField
                  fullWidth
                  label="Barcode"
                  defaultValue=""
                  name="barcode"
                  {...register("barcode")}
                  variant="outlined"
                  margin="dense"
                  multiline
                  error={!!errors.barcode}
                  onInput={
                    (input) => {
                      if (input.target.value) {
                        setCurrentType(codeType.barcode)
                      }
                    }
                  }
                />
                <FormHelperText>
                  Barcodes are generated in the CODE-128 standard,  enclosing the code with * (e.g. *MY BARCODE*) will render the code in CODE-39 standard.
                </FormHelperText>
                {
                  errors.barcode &&
                  <FormHelperText error>
                    {errors.barcode.message}
                  </FormHelperText>
                }
              </FormControl>
            }
          </Box>
          <Controller
  name="qrCodeDefined"
  control={control}
  render={({ field }) => (
    <FormControlLabel
      control={
        <Checkbox
          {...field}  
          checked={field.value}  
          disabled={currentValues.barcodeDefined}
          onChange={(e) => {
            const currentValue = e.target.checked;
            field.onChange(currentValue); 
            updateSelectionError(codeType.qrCode, currentValue);
            if (!currentValue) {
              setValue("qrCode", "");
              setCurrentType(getDefinedType(codeType.qrCode));
            } else {
              if (noneSelected()) {
                setCurrentType(codeType.qrCode);
              }
            }
          }}
        />
      }
      label="I have a QR code"
    />
  )}
/>

          {
            currentValues.qrCodeDefined &&
            <FormControl fullWidth variant="outlined" error={!!errors.qrCode}>
              <TextField
                fullWidth
                label="QR code"
                defaultValue=""
                name="qrCode"
                {...register("qrCode")}
                variant="outlined"
                margin="dense"
                multiline
                error={!!errors.qrCode}
                onInput={
                  (input) => {
                    if (input.target.value) {
                      setCurrentType(codeType.qrCode)
                    }
                  }
                }
              />
              {
                errors.qrCode &&
                <FormHelperText error>
                  {errors.qrCode.message}
                </FormHelperText>
              }
            </FormControl>
          }
        </Grid>
        {/* change to false to remove barcode preview */}
        {
          true &&
          <Grid item xs={12} md={6}>
            <RedemptionCodeDisplay
              codes={{
                promoCode: hasDefinedCode(codeType.promoCode) ? currentValues.promoCode || values.code.promoCode : '',
                barcode: hasDefinedCode(codeType.barcode) && !errors.barcode ? currentValues.barcode || values.code.barcode : '',
                qrCode: hasDefinedCode(codeType.qrCode) ? currentValues.qrCode || values.code.qrCode : '',
              }}
              enabledTypes={Object.keys(codeType).filter((type) => currentValues[`${type}Defined`])}
              activeType={Object.keys(codeType).find((type) => codeType[type] === currentType)}
              onTypeChange={(type) => setCurrentType(codeType[type])}
            />
          </Grid>
        }
        
      </Grid>
      {selectionError && (
        <FormHelperText error sx={{ mt: 2 }}>
          {selectionError}
        </FormHelperText>
      )}
      <Box mt={2} mb={1} display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
        <OfferSaveActions
          onUpdate={onUpdate
            ? handleSubmit((data) => onUpdate({ code: getCodeValue(data) }))
            : undefined}
          onSave={() => onSave({ code: getCodeValue(getValues()) })}
          saveButtonLabel={saveButtonLabel}
        />
        <Box>
          <WizardBackButton onClick={back} />
          <Button
            color="primary"
            onClick={() => handleSubmit(handleFormSubmit)()}
            variant="contained"
            type="submit"
          >
            Next
          </Button>
        </Box>
      </Box>
    </form>
    </>
  );
}

export default OfferCode
